import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Users, X } from 'lucide-react';
import type { PartyState, SocialNotification, SocialProfile } from '@teaplus/shared';
import { ProfileAvatar } from '../profile/avatar';
import { Button } from '../ui/button';
import { api, API_EVENTS, errorMessage } from '@/lib/api';
import { addToInbox, markInboxRead, refreshInbox } from '@/lib/inbox';
import { looked } from '@/lib/friend-looks';
import { hostStreamOf, nowPlaying } from '@/lib/now-playing';
import { partyWatchPath, usePartyEnabled } from '@/lib/party';
import { playerChatShown, receivePlayerChat } from '@/lib/player-chat';
import {
  notificationAudible,
  notificationHidden,
  playChatTone,
  playNotificationTone,
} from '@/lib/notification-sound';
import { LIVE_CONNECTED } from '@/lib/presence';
import { useActiveProfile, usePreferences } from '@/lib/queries';
import {
  chatPath,
  chatToastId,
  isChatOpen,
  markChatsDelivered,
  refreshChats,
  requestToJoin,
  sk,
  useCanSocialise,
  useLooks,
} from '@/lib/social';

const titlePath = (type: string, id: string) =>
  type === 'movie' ? `/movie/${id}` : `/series/${id}`;

interface CardAction {
  label: string;
  onClick: () => void | Promise<void>;
  primary?: boolean;
}

/** A friend notification: who, what, the title's poster, and what you can do about it. */
function NotificationCard({
  toastId,
  who,
  text,
  detail,
  poster,
  icon,
  actions,
}: {
  toastId: string | number;
  who: SocialProfile;
  text: ReactNode;
  detail?: string | null;
  poster?: string | null;
  icon?: ReactNode;
  actions: CardAction[];
}) {
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <div
      role="status"
      className="relative flex w-[min(380px,calc(100vw-32px))] gap-3 rounded-2xl border border-line bg-elevated p-3.5 pr-9 font-sans text-text shadow-2xl shadow-black/50"
    >
      <div className="relative shrink-0 self-start">
        <ProfileAvatar avatar={who.avatar} image={who.avatarUrl} size={40} rounded="full" />
        {icon && (
          <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-pink text-on-pink ring-2 ring-elevated [&>svg]:h-3 [&>svg]:w-3">
            {icon}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">
          <span className="font-semibold">{who.name}</span> {text}
        </p>
        {detail && <p className="mt-0.5 line-clamp-2 text-xs text-muted">{detail}</p>}
        {actions.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-2">
            {actions.map((a) => (
              <Button
                key={a.label}
                size="sm"
                variant={a.primary ? 'primary' : 'secondary'}
                className="h-8 px-3 text-[13px]"
                loading={busy === a.label}
                disabled={!!busy}
                onClick={async () => {
                  setBusy(a.label);
                  try {
                    await a.onClick();
                    toast.dismiss(toastId);
                  } catch (e) {
                    toast.error(errorMessage(e));
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                {a.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      {poster && (
        <img
          loading="lazy"
          decoding="async"
          src={poster}
          alt=""
          className="h-16 w-11 shrink-0 rounded-lg object-cover ring-1 ring-white/10"
        />
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => toast.dismiss(toastId)}
        className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-text"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** No ping from the server for this long means the connection is dead. */
const STALE_MS = 40_000;

const episodeOf = (m: { season: number | null; episode: number | null }) =>
  m.season != null && m.episode != null ? `S${m.season} E${m.episode}` : null;

/**
 * Listens for friends' notifications while the app is open and shows them in the corner:
 * shared titles, who is watching what, friend requests, and Request to Join.
 */
export function NotificationCenter() {
  const can = useCanSocialise();
  const partyOn = usePartyEnabled();
  const prefs = usePreferences();
  useLooks();
  const me = useActiveProfile().data?.profile.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const handle = useRef<(n: SocialNotification) => void>(() => undefined);

  handle.current = (raw) => {
    // A receipt, or something from another of your windows: the chat refreshes, nothing more.
    // From one of your own windows it could be any chat, so every open one refreshes.
    if (raw.type === 'chat-update') {
      refreshChats(qc, raw.from.id === me ? undefined : raw.from.id);
      return;
    }
    // Your nickname and picture for this friend, if you gave them one.
    const n = { ...raw, from: looked(raw.from) } as SocialNotification;
    void qc.invalidateQueries({ queryKey: sk.counts });
    void qc.invalidateQueries({ queryKey: sk.friends });
    void qc.invalidateQueries({ queryKey: sk.activity });
    if (n.type === 'recommendation') void qc.invalidateQueries({ queryKey: sk.recs });
    // A message in the chat that's open is read already, so the bell doesn't count it.
    const seen = n.type === 'chat-message' && isChatOpen(n.from.id);
    addToInbox(qc, raw, seen);
    if (seen) void markInboxRead(qc, [n.id]).catch(() => undefined);
    if (n.type === 'recommendation' || n.type === 'chat-message') {
      refreshChats(qc, n.from.id);
      markChatsDelivered(qc);
      // Already on screen in the open chat.
      if (isChatOpen(n.from.id)) return;
    }
    const hidden = notificationHidden(n.type, prefs, !!nowPlaying());
    const ding = () => {
      if (hidden || !notificationAudible(n.type, prefs)) return;
      if (n.type === 'chat-message') playChatTone();
      else playNotificationTone();
    };
    // During a film (not a party), a message goes to the player's chat, to reply without leaving.
    if (n.type === 'chat-message' && playerChatShown()) {
      ding();
      receivePlayerChat({ id: n.id, from: n.from, body: n.body, attachment: n.attachment }, hidden);
      return;
    }
    // A reply to your own Request to Join still comes through: you're waiting for it.
    if (hidden && n.type !== 'join-reply') return;
    const show = (
      render: (id: string | number) => ReactElement,
      duration = 8000,
      id: string = n.id,
    ) => {
      ding();
      toast.custom(render, { id, duration, unstyled: true });
    };

    switch (n.type) {
      case 'recommendation':
        show((id) => (
          <NotificationCard
            toastId={id}
            who={n.from}
            text={
              <>
                shared <span className="font-semibold">{n.media.title}</span> with you
              </>
            }
            detail={n.message ? `“${n.message}”` : null}
            poster={n.media.posterUrl}
            actions={[
              {
                label: 'View',
                primary: true,
                onClick: () => navigate(titlePath(n.media.contentType, n.media.mediaId)),
              },
            ]}
          />
        ));
        break;
      case 'chat-message':
        show(
          (id) => (
            <NotificationCard
              toastId={id}
              who={n.from}
              text={
                n.attachment === 'gif'
                  ? 'sent you a GIF'
                  : n.attachment === 'image'
                    ? 'sent you a picture'
                    : 'sent you a message'
              }
              detail={n.body || null}
              actions={[
                { label: 'Reply', primary: true, onClick: () => navigate(chatPath(n.from.id)) },
              ]}
            />
          ),
          8000,
          chatToastId(n.from.id),
        );
        break;
      case 'watching':
        show((id) => (
          <NotificationCard
            toastId={id}
            who={n.from}
            text={
              <>
                is watching <span className="font-semibold">{n.media.title}</span>
              </>
            }
            detail={episodeOf(n.media)}
            poster={n.media.posterUrl}
            actions={[
              {
                label: 'View',
                onClick: () => navigate(titlePath(n.media.contentType, n.media.mediaId)),
              },
              ...(partyOn
                ? [
                    {
                      label: 'Request to Join',
                      primary: true,
                      onClick: async () => {
                        await requestToJoin(n.from);
                        toast(
                          `Asked ${n.from.name} to watch together. Waiting for them to answer…`,
                        );
                      },
                    },
                  ]
                : []),
            ]}
          />
        ));
        break;
      case 'friend-request':
        show((id) => (
          <NotificationCard
            toastId={id}
            who={n.from}
            text="sent you a friend request"
            actions={[
              { label: 'View', primary: true, onClick: () => navigate('/friends?tab=manage') },
            ]}
          />
        ));
        break;
      case 'friend-accepted':
        show((id) => (
          <NotificationCard
            toastId={id}
            who={n.from}
            text="accepted your friend request"
            actions={[{ label: 'View', onClick: () => navigate('/friends') }]}
          />
        ));
        break;
      case 'join-request': {
        if (!partyOn) break;
        const left = Math.max(5000, Date.parse(n.expiresAt) - Date.now());
        show(
          (id) => (
            <NotificationCard
              toastId={id}
              who={n.from}
              icon={<Users />}
              text="wants to watch together"
              detail={`Watch ${n.media.title}${episodeOf(n.media) ? ` ${episodeOf(n.media)}` : ''} together`}
              poster={n.media.posterUrl}
              actions={[
                {
                  label: 'Accept',
                  primary: true,
                  onClick: async () => {
                    // Watching right now: the party starts where you are and your stream carries on.
                    const np = nowPlaying();
                    const live = np && !np.inParty ? np : null;
                    const state = await api<PartyState>(
                      `/api/party/requests/${n.requestId}/accept`,
                      {
                        body: live
                          ? {
                              media: {
                                type: live.type,
                                id: live.id,
                                season: live.season,
                                episode: live.episode,
                              },
                              position: live.position(),
                              playing: live.playing(),
                              stream: hostStreamOf(live),
                            }
                          : {},
                      },
                    );
                    if (live) live.joinParty(state.code);
                    else navigate(`/party/${state.code}`);
                    toast(`Watch Together started with ${n.from.name}`);
                  },
                },
                {
                  label: 'Decline',
                  onClick: async () => {
                    await api(`/api/party/requests/${n.requestId}/decline`, { method: 'POST' });
                  },
                },
              ]}
            />
          ),
          left,
        );
        break;
      }
      case 'party-invite':
        if (!partyOn) break;
        show(
          (id) => (
            <NotificationCard
              toastId={id}
              who={n.from}
              icon={<Users />}
              text="invited you to a Party"
              actions={[
                { label: 'Join', primary: true, onClick: () => navigate(`/party/${n.code}`) },
              ]}
            />
          ),
          30_000,
        );
        break;
      case 'join-reply':
        if (n.accepted && n.code && n.media) {
          ding();
          toast(`${n.from.name} said yes. Joining…`);
          navigate(n.live ? partyWatchPath(n.media, n.code) : `/party/${n.code}`);
        } else {
          show((id) => (
            <NotificationCard
              toastId={id}
              who={n.from}
              text="can't watch together right now"
              actions={[]}
            />
          ));
        }
        break;
    }
  };

  useEffect(() => {
    if (!can || typeof EventSource === 'undefined') return;
    let es: EventSource | null = null;
    let heard = Date.now();
    let lastId = '';
    const refresh = () => {
      void qc.invalidateQueries({ queryKey: sk.counts });
      void qc.invalidateQueries({ queryKey: sk.friends });
      void qc.invalidateQueries({ queryKey: sk.activity });
      refreshChats(qc);
      markChatsDelivered(qc);
      refreshInbox(qc);
    };
    const connect = () => {
      es?.close();
      heard = Date.now();
      // A fresh connection after a stall picks up anything missed since the last one we got.
      const url = lastId
        ? `/api/notifications/events?lastEventId=${encodeURIComponent(lastId)}`
        : '/api/notifications/events';
      const src = (es = new EventSource(url, { withCredentials: true }));
      // Whenever it (re)connects, catch up on anything that changed meanwhile.
      src.onopen = () => {
        heard = Date.now();
        refresh();
        // Tell friends what you're doing again, in case the gap made you look offline.
        window.dispatchEvent(new Event(LIVE_CONNECTED));
      };
      src.addEventListener('ping', () => (heard = Date.now()));
      // Another of your windows read or cleared some of the bell's notifications.
      src.addEventListener('inbox', () => {
        heard = Date.now();
        refreshInbox(qc);
      });
      // The owner restricted (or lifted) this account or profile: check straight away.
      src.addEventListener('account', () => {
        heard = Date.now();
        window.dispatchEvent(new Event(API_EVENTS.restricted));
      });
      src.onmessage = (e) => {
        heard = Date.now();
        if (e.lastEventId) lastId = e.lastEventId;
        try {
          handle.current(JSON.parse(e.data) as SocialNotification);
        } catch {}
      };
    };
    connect();
    // The server pings every 15 seconds; a connection that's gone quiet is dead, so start over.
    const watchdog = window.setInterval(() => {
      if (Date.now() - heard > STALE_MS) connect();
    }, 5_000);
    // Back online, or back to the window after sleep: reconnect straight away.
    const wake = () => {
      if (document.visibilityState === 'visible' && Date.now() - heard > 20_000) connect();
    };
    window.addEventListener('online', connect);
    document.addEventListener('visibilitychange', wake);
    return () => {
      window.clearInterval(watchdog);
      window.removeEventListener('online', connect);
      document.removeEventListener('visibilitychange', wake);
      es?.close();
    };
  }, [can, qc]);

  return null;
}
